import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FacetedSearch } from './FacetedSearch';
import {
  query,
  setTotalCount,
  submitSearchFacet,
  updateFilterOption,
} from '../Actions/FacetedSearch.action';
import { SortCriteria } from './SortCriteria';
import FilterTag, { FilterTags } from './FilterTags';
import { PRODUCT_VIEW_CACHED } from '../constants';
import { useDebounce } from '../Hooks/useDebounce';
import ClearFiltersButton from './ClearFiltersButton';
import { translate } from '../Services/translation';

function searchResultDataChange(dom, onCompactFilterUpdated) {
  if (dom == null) {
    return;
  }

  const container = document.createElement('div');
  container.innerHTML = dom;

  const existingResult = document.getElementById('search-result');
  const tempResult = container.querySelector('#search-result');
  if (!existingResult || !tempResult) {
    return;
  }

  const existingFilter = existingResult.querySelector('#facetedSearchCompact');
  const tempFilter = tempResult.querySelector('#facetedSearchCompact');

  if (tempFilter && existingFilter) {
    const totalCount = Number(tempFilter.dataset.paginationTotal) || 0;
    existingFilter.dataset.paginationTotal = String(totalCount);
    onCompactFilterUpdated(totalCount);
  }

  function replaceNode(node, newNode) {
    node.parentNode.replaceChild(newNode, node);
  }

  // move existingFilter from existingResult to tempResult
  replaceNode(tempFilter, existingFilter);

  // replace existingResult with tempResult ( newResult )
  replaceNode(existingResult, tempResult);

  // bootstrap react components if any exists in the search result
  window.__litium.bootstrapComponents();
}

export function FacetedSearchContainer() {
  const dispatch = useDispatch();
  const {
    sortCriteria,
    facetFilters: globalFacetFilters,
    navigationTheme,
    productsViewCachedId,
    totalCount,
  } = useSelector((state) => state.facetedSearch);

  const [facetFilters, setFacetFilters] = useState(globalFacetFilters || []);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const previousCachedId = useRef(productsViewCachedId);

  const submitSearchFacetDebounced = useDebounce((updatedFilters) => {
    dispatch(submitSearchFacet(updatedFilters));
  }, 500);

  const onFacetChange = useCallback(
    (filter, option) => {
      const updatedFilters = updateFilterOption(facetFilters, filter, option);

      setFacetFilters(updatedFilters);
      submitSearchFacetDebounced(updatedFilters);
    },
    [setFacetFilters, facetFilters]
  );

  useEffect(() => {
    if (previousCachedId.current === productsViewCachedId) {
      return;
    }

    setFacetFilters(globalFacetFilters);
    previousCachedId.current = productsViewCachedId;
  }, [globalFacetFilters, productsViewCachedId]);

  useEffect(() => {
    dispatch(query(window.location.search.substring(1) || '', false));
  }, [dispatch]);

  useEffect(() => {
    const productViewCached =
      window.__litium?.cache?.[PRODUCT_VIEW_CACHED] ?? {};

    if (productViewCached.used) {
      return;
    }

    productViewCached.used = true;
    searchResultDataChange(productViewCached.productsView, (totalCount) => {
      dispatch(setTotalCount(totalCount));
    });
  });

  useEffect(() => {
    document.body.classList.toggle('filter-flyout-open', isFlyoutOpen);
    return () => document.body.classList.remove('filter-flyout-open');
  }, [isFlyoutOpen]);

  const activeFilters =
    facetFilters?.reduce(
      (acc, filter) => acc + (filter.selectedOptions?.length ?? 0),
      0
    ) ?? 0;

  const hasFilters = facetFilters?.length > 0;

  return (
    <div className="container">
      <div className="compact-filter">
        <div className="compact-filter__row row align-bottom justify-between">
          <div className="columns filter-col">
            {hasFilters && (
              <div className="compact-filter__actions">
                <button
                  className="filter-flyout__trigger"
                  onClick={() => setIsFlyoutOpen(true)}
                  aria-expanded={isFlyoutOpen}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="18"
                    viewBox="0 0 25 18"
                  >
                    <line
                      x1="2"
                      y1="4"
                      x2="23"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <line
                      x1="16"
                      y1="1"
                      x2="16"
                      y2="7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />

                    <line
                      x1="2"
                      y1="14"
                      x2="23"
                      y2="14"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8"
                      y1="11"
                      x2="8"
                      y2="17"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <p>{translate('facet.header.filter')}</p>
                  {activeFilters > 0 ? <p>({activeFilters})</p> : ''}
                </button>
              </div>
            )}
          </div>
          {totalCount > 0 && (
            <div className="columns filter-col text-center">
              {totalCount} {translate('filter.totalproducts')}
            </div>
          )}
          <div className="columns filter-col">
            <SortCriteria sortCriteria={sortCriteria?.sortItems} />
          </div>
        </div>
      </div>

      <div
        className={`filter-flyout__overlay${
          isFlyoutOpen ? ' filter-flyout__overlay--visible' : ''
        }`}
        onClick={() => setIsFlyoutOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`filter-flyout__panel${
          isFlyoutOpen ? ' filter-flyout__panel--open' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={translate('facet.header.filter')}
      >
        <div className="filter-flyout__header">
          <span className="filter-flyout__title">
            {translate('facet.header.filter')}
          </span>
          <button
            className="filter-flyout__close"
            onClick={() => setIsFlyoutOpen(false)}
            aria-label="Close filters"
          >
            &times;
          </button>
        </div>
        <div className="filter-flyout__content">
          <FacetedSearch
            facetFilters={facetFilters}
            onFacetChange={onFacetChange}
          />
          <FilterTags
            facetFilters={facetFilters}
            navigationTheme={navigationTheme}
          />
        </div>
        <div className="filter-flyout__footer">
          <ClearFiltersButton className="btn btn--transparent" />
          <button
            className="btn btn--transparent"
            onClick={() => setIsFlyoutOpen(false)}
          >
            {translate('filter.showresults')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FacetedSearchContainer;
